// One validator for the editor, the API and the CLI (plan §2: shared validation).
const { validTextStyles } = require("./styles");
const { validBlocks } = require("./structure");

const REF = /^ref:[0-9a-f]{64}$/;
const IMAGE_DATA = /^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/=]+$/;
const FONT_DATA = /^data:font\/woff2;base64,[A-Za-z0-9+/=]+$/;
const imageSource = (src) => typeof src === "string" && (IMAGE_DATA.test(src) || REF.test(src));

const roles = [
  "title",
  "subtitle",
  "section",
  "dish",
  "description",
  "price",
  "note",
  "decoration",
];
const finite = (value) => Number.isFinite(value) && Math.abs(value) < 100000;
const color = (value) =>
  typeof value === "string" && /^#[0-9a-f]{6}$/i.test(value);
function styleValid(style, required = false) {
  if (!style || typeof style !== "object" || Array.isArray(style)) return false;
  if (required && (!finite(style.fontSize) || style.fontSize <= 0))
    return false;
  for (const [key, value] of Object.entries(style)) {
    if (
      [
        "fontSize",
        "lineHeight",
        "letterSpacing",
        "strokeWidth",
        "radius",
        "fontWeight",
      ].includes(key) &&
      !finite(value)
    )
      return false;
    if (["fill", "stroke"].includes(key) && !color(value)) return false;
    if (
      key === "fontFamily" &&
      (typeof value !== "string" || value.length > 100)
    )
      return false;
    if (key === "align" && !["left", "center", "right"].includes(value))
      return false;
    if (key === "fontStyle" && !["normal", "italic"].includes(value))
      return false;
  }
  return true;
}
function validateDocument(doc) {
  if (
    !doc ||
    doc.version !== 1 ||
    typeof doc.name !== "string" ||
    doc.name.length > 200 ||
    !["dinner", "takeout", "lunch", "wine"].includes(doc.menuKey) ||
    !Array.isArray(doc.pages) ||
    doc.pages.length < 1 ||
    doc.pages.length > 40 ||
    !doc.styles
  )
    return false;
  if (
    doc.fonts !== undefined &&
    (!Array.isArray(doc.fonts) ||
      doc.fonts.length > 30 ||
      !doc.fonts.every(
        (f) =>
          /^[a-zA-Z0-9 _-]{1,100}$/.test(f.family) &&
          /^(normal|italic)$/.test(f.style) &&
          /^\d{3}( \d{3})?$/.test(String(f.weight)) &&
          typeof f.src === "string" && (FONT_DATA.test(f.src) || REF.test(f.src))
      ))
  )
    return false;
  if (!roles.every((role) => styleValid(doc.styles[role], true))) return false;
  if (!validStyleGroups(doc.styleGroups)) return false;
  if (!validTextStyles(doc.textStyles, roles)) return false;
  if (doc.format !== undefined && ![1, 2].includes(doc.format)) return false;
  if (doc.schema !== undefined && ![1, 2].includes(doc.schema)) return false;
  if (!validBlocks(doc)) return false;
  // Generated designs carry an empty legacy asset list.
  if (doc.assets !== undefined && !(Array.isArray(doc.assets) && doc.assets.length === 0) && (
    !doc.assets || typeof doc.assets !== "object" || Array.isArray(doc.assets) ||
    Object.entries(doc.assets).some(([key, src]) => !/^art[0-9]+$/.test(key) || !imageSource(src))
  )) return false;
  const ids = new Set();
  return doc.pages.every((page) => {
    if (
      typeof page.id !== "string" ||
      ids.has(page.id) ||
      typeof page.name !== "string" ||
      !finite(page.width) ||
      !finite(page.height) ||
      page.width < 72 ||
      page.height < 72 ||
      page.width > 3000 ||
      page.height > 3000 ||
      !finite(page.margin) ||
      page.margin < 0 ||
      !color(page.background) ||
      !Array.isArray(page.elements) ||
      page.elements.length > 1500 ||
      !validLayerGroups(page.elements)
    )
      return false;
    ids.add(page.id);
    return page.elements.every((node) => {
      if (
        typeof node.id !== "string" ||
        ids.has(node.id) ||
        !["text", "rect", "ellipse", "line", "image"].includes(node.type) ||
        !roles.includes(node.role) ||
        !["x", "y", "w", "h", "rotation", "opacity"].every((k) =>
          finite(node[k])
        ) ||
        node.w <= 0 ||
        node.h <= 0 ||
        node.opacity < 0 ||
        node.opacity > 1 ||
        !styleValid(node.style) ||
        (node.styleRef !== undefined && !(typeof node.styleRef === "string" && doc.textStyles && Object.prototype.hasOwnProperty.call(doc.textStyles, node.styleRef)))
      )
        return false;
      if (
        node.originalLayout &&
        (!Array.isArray(node.originalLayout.runs) ||
          node.originalLayout.runs.length > 10000 ||
          !node.originalLayout.runs.every(
            (r) =>
              typeof r.text === "string" &&
              r.text.length <= 20000 &&
              finite(r.x) &&
              finite(r.y)
          ))
      )
        return false;
      ids.add(node.id);
      if (
        node.type === "text" &&
        (typeof node.text !== "string" ||
          node.text.length > 20000 ||
          (node.textOverride !== undefined &&
            (typeof node.textOverride !== "string" ||
              node.textOverride.length > 20000)))
      )
        return false;
      if (
        node.type === "image" &&
        !imageSource(
          node.src || (typeof node.assetId === "string" && Object.prototype.hasOwnProperty.call(doc.assets || {}, node.assetId) ? doc.assets[node.assetId] : "")
        )
      )
        return false;
      return true;
    });
  });
}

function validStyleGroups(groups) {
  if (groups === undefined) return true;
  if (!groups || typeof groups !== "object" || Array.isArray(groups))
    return false;
  return Object.entries(groups).every(
    ([role, group]) =>
      (roles.includes(role) || /^custom:[a-zA-Z0-9_-]{1,100}$/.test(role)) &&
      role !== "decoration" &&
      group &&
      typeof group === "object" &&
      !Array.isArray(group) &&
      typeof group.name === "string" &&
      group.name.trim().length > 0 &&
      group.name.length <= 100 &&
      [group.included, group.excluded].every(
        (ids) =>
          Array.isArray(ids) &&
          ids.length <= 60000 &&
          ids.every(
            (id) => typeof id === "string" && id.length > 0 && id.length <= 200
          )
      )
  );
}

// Nested layer groups: ordered outer-to-inner, acyclic, consistent parents.
function validLayerGroups(elements) {
  const parents = new Map(), ids = new Set(elements.map((n) => n.id));
  return elements.every((node) => {
    if (node.groupPath === undefined) return !node.groupId || typeof node.groupId === "string";
    if (!Array.isArray(node.groupPath) || node.groupPath.length > 64) return false;
    const seen = new Set();
    return node.groupPath.every((group, index) => {
      if (!group || typeof group.id !== "string" || !group.id || ids.has(group.id) || seen.has(group.id) || typeof group.name !== "string" || !group.name.trim() || group.name.length > 100) return false;
      seen.add(group.id);
      const parent = index > 0 ? node.groupPath[index - 1].id : null;
      if (parents.has(group.id) && parents.get(group.id) !== parent) return false;
      parents.set(group.id, parent);
      return true;
    }) && (node.groupId || null) === ((node.groupPath[node.groupPath.length - 1] || {}).id || null);
  });
}

module.exports = { validateDocument, validLayerGroups, styleValid, ROLES: roles, REF, IMAGE_DATA, FONT_DATA };
